// game/index.js - Main game controller
import Controls from './Controls';
import Renderer from './Renderer';
import Physics from './Physics';
import gameMap from './Map';
import { Pacman, Ghost, createFoods } from './Entity';
import * as api from '../../../api';
import { getSessionId, getGuestName } from '../../../utils/session';

class PacmanGame {
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.gridSize = 20;
    this.tileSize = 20;
    this.isGameOver = false;
    this.gameLoop = null;
    this.score = 0;
    this.highScore = this.loadHighScore();

    // Initialize game components
    this.initializeGameState();
    
    // Initialize game
    this.start();
  }
  
  initializeGameState() {
    // Set up game map and entities
    this.map = gameMap;
    this.pacman = new Pacman();
    this.ghost = new Ghost();
    this.foods = createFoods(this.map);

    // Initialize subsystems
    this.physics = new Physics(this.map, this.tileSize);
    this.renderer = new Renderer(this.ctx, this.tileSize);
    this.controls = new Controls(this.canvas, this.pacman);

    // Initialize controls
    this.controls.init();
  }

  update() {
    // Update entities
    this.physics.movePacman(this.pacman, this.ghost, this.foods);
    this.physics.moveGhost(this.ghost, this.pacman);

    // Check for ghost collision
    if (this.physics.checkCollision(this.pacman, this.ghost)) {
      if (this.pacman.isPowered) {
        // Reset ghost position if pacman is powered
        this.ghost.x = 13;
        this.ghost.y = 8;
        this.updateScore(200); // Bonus points for eating ghost
      } else {
        this.endGame();
        return; // Exit early to prevent further updates
      }
    }

    // Check if food was eaten and update score
    this.checkFoodStatus();

    // Check if all food is eaten
    this.checkLevelComplete();

    // Render the game or game over screen
    if (this.isGameOver) {
      this.renderer.showGameOver(this.canvas, this.score, this.highScore);
    } else {
      this.draw();
    }
  }
  
  checkFoodStatus() {
    // Check each food item to see if it was just eaten
    Object.values(this.foods).forEach(food => {
      if (food.eaten && !food.scoreAdded) {
        food.scoreAdded = true; // Mark that we've counted this food

        // Different food items give different points
        switch(food.type) {
          case 'dot':
            this.updateScore(10); // Small points for dots
            break;
          case 'fruit':
            this.updateScore(100); // Bonus points for special food
            break;
          case 'burger':
            this.updateScore(200); // Bonus points for special food
            break;
          case 'pizza':
            this.updateScore(300); // Bonus points for special food
            break;
        }
      }
    });
  }
  
  checkLevelComplete() {
    // Check if all foods have been eaten (excluding dots for level completion)
    const specialFoods = Object.values(this.foods).filter(food => food.type !== 'dot');
    const dots = Object.values(this.foods).filter(food => food.type === 'dot');

    const allSpecialFoodsEaten = specialFoods.every(food => food.eaten);
    const allDotsEaten = dots.every(dot => dot.eaten);

    if (allSpecialFoodsEaten && allDotsEaten) {
      // Add completion bonus
      this.updateScore(1000);

      // Reset food for next level but keep score
      this.foods = createFoods(this.map);

      // Reset positions
      this.pacman.reset();
      this.ghost.reset();
    }
  }
  
  updateScore(points) {
    this.score += points;
    
    // Update high score if necessary
    if (this.score > this.highScore) {
      this.highScore = this.score;
      this.saveHighScore();
    }
  }
  
  loadHighScore() {
    const savedScore = localStorage.getItem('pacmanHighScore');
    return savedScore ? parseInt(savedScore) : 0;
  }
  
  saveHighScore() {
    localStorage.setItem('pacmanHighScore', this.highScore.toString());
  }
  
  draw() {
    // Clear canvas
    this.renderer.clear(this.canvas);
    
    // Draw game elements
    this.renderer.drawMap(this.map);
    this.renderer.drawFood(this.foods);
    this.renderer.drawPacman(this.pacman);
    this.renderer.drawGhost(this.ghost);
    
    // Draw score
    this.drawScore();
  }
  
  drawScore() {
    this.ctx.fillStyle = 'white';
    this.ctx.font = '16px Arial';
    this.ctx.textAlign = 'left';
    this.ctx.fillText(`Score: ${this.score}`, 10, this.canvas.height - 10);
    
    this.ctx.textAlign = 'right';
    this.ctx.fillText(`High Score: ${this.highScore}`, this.canvas.width - 10, this.canvas.height - 10);
  }

  endGame() {
    this.isGameOver = true;
    this.stop();

    // Save score to backend
    this.saveScore();

    // Add restart event listener
    const restartHandler = () => {
      this.canvas.removeEventListener('click', restartHandler);
      this.restart();
    };
    this.canvas.addEventListener('click', restartHandler);

    // Show game over screen after a brief delay to ensure it's visible
    setTimeout(() => {
      this.renderer.showGameOver(this.canvas, this.score, this.highScore);
    }, 100);
  }

  async saveScore() {
    try {
      const scoreData = {
        gameType: 'pacman',
        score: this.score
      };

      // Check if user is logged in (from localStorage)
      const profileString = localStorage.getItem('profile');
      const profile = profileString ? JSON.parse(profileString) : null;

      if (profile && profile.result && profile.result._id) {
        // Authenticated user
        scoreData.playerType = 'user';
        scoreData.userId = profile.result._id;
      } else {
        // Guest user
        scoreData.playerType = 'guest';
        scoreData.sessionId = getSessionId();
        scoreData.guestName = getGuestName();
      }

      await api.saveGameScore(scoreData);
      console.log('Pacman score saved successfully!');
    } catch (error) {
      console.error('Failed to save Pacman score:', error);
    }
  }

  restart() {
    // Reset game state
    this.isGameOver = false;
    this.score = 0;

    // Reset entities
    this.pacman.reset();
    this.ghost.reset();
    this.foods = createFoods(this.map);

    this.start();
  }

  start() {
    // Clear any existing game loop
    if (this.gameLoop) clearInterval(this.gameLoop);
    
    // Start new game loop at ~60fps
    this.gameLoop = setInterval(() => this.update(), 16);
  }

  stop() {
    if (this.gameLoop) clearInterval(this.gameLoop);
  }
}

export default PacmanGame; 